import { stateLabel, kindLabel } from './display-copy';
import type { MenuAudience } from './navigation-model';
import { useState } from 'react';
import type { OemWorkspace } from '../../lib/oem-client';
import { displayValue } from '../../lib/oem-display';
import { forms } from './record-editor';
import {
  matchingWork,
  workDestination,
  workPage,
  workState,
  type WorkFilters,
  type WorkSort,
} from './work-list-model';
const initial: WorkFilters = { query: '', sku: '', state: '', sort: 'newest' };
const formPages = Object.fromEntries(
  Object.entries(forms).map(([key, definition]) => [definition.kind, key]),
);
export function WorkList({
  workspace,
  link,
  audience = 'staff',
}: {
  workspace: OemWorkspace;
  link: (key: string) => string;
  audience?: MenuAudience;
}) {
  const [filters, setFilters] = useState(initial);
  const [page, setPage] = useState(1);
  const result = workPage(matchingWork(workspace.records, workspace.skus, filters), page);
  const states = [...new Set(workspace.records.map(workState))].sort();
  function change<K extends keyof WorkFilters>(key: K, value: WorkFilters[K]) {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  }
  const reset = () => {
    setFilters(initial);
    setPage(1);
  };
  return (
    <section className="card work-list" aria-label="รายการงานในโครงการนี้">
      <div className="between">
        <h2>รายการงานในโครงการนี้</h2>
        <button type="button" className="text-button" onClick={reset}>
          ล้างตัวกรอง
        </button>
      </div>
      <p className="muted">ค้นหาจากข้อมูลที่ได้รับสิทธิ์ · แสดงทุกเวอร์ชันที่อยู่ในโครงการนี้</p>
      <div className="work-filters">
        <label>
          ค้นหารายการ
          <input
            type="search"
            value={filters.query}
            onChange={(e) => change('query', e.target.value)}
            placeholder="ชื่อ หัวข้อ หรือ SKU"
          />
        </label>
        <label>
          SKU
          <select value={filters.sku} onChange={(e) => change('sku', e.target.value)}>
            <option value="">ทุก SKU</option>
            <option value="project">งานรวมของโครงการ</option>
            {workspace.skus.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          สถานะ
          <select value={filters.state} onChange={(e) => change('state', e.target.value)}>
            <option value="">ทุกสถานะ</option>
            {states.map((state) => (
              <option key={state} value={state}>
                {stateLabel(state)}
              </option>
            ))}
          </select>
        </label>
        <label>
          เรียงตาม
          <select value={filters.sort} onChange={(e) => change('sort', e.target.value as WorkSort)}>
            <option value="newest">ล่าสุดก่อน</option>
            <option value="oldest">เก่าก่อน</option>
            <option value="label">ชื่อรายการ</option>
            <option value="version">เวอร์ชันสูงก่อน</option>
          </select>
        </label>
      </div>
      <p role="status">
        พบ {result.total} รายการ · หน้า {result.current} / {result.pages}
      </p>
      {result.total === 0 ? (
        <div className="work-empty">
          <p>ไม่พบรายการที่ตรงกับตัวกรอง</p>
          <button type="button" className="action secondary" onClick={reset}>
            แสดงทุกรายการ
          </button>
        </div>
      ) : (
        <div className="work-table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>หัวข้อ / SKU</th>
                <th>เวอร์ชัน</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {result.records.map((record) => {
                const destination = workDestination(record, formPages, audience);
                return (
                  <tr key={record.id}>
                    <td>
                      {destination ? (
                        <a href={link(destination)}>
                          {displayValue(record.content.label ?? kindLabel(record.kind))}
                        </a>
                      ) : (
                        <span>{displayValue(record.content.label ?? kindLabel(record.kind))}</span>
                      )}
                      <p className="muted">
                        {workspace.skus.find((s) => s.id === record.skuId)?.label ??
                          'งานรวมของโครงการ'}
                      </p>
                    </td>
                    <td>
                      v{record.version}
                      <p className="version">{record.hash.slice(0, 12)}</p>
                    </td>
                    <td>
                      {stateLabel(workState(record))}
                      <p className="muted">{displayValue(record.content.summary ?? '')}</p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {result.pages > 1 && (
        <div className="work-paging" aria-label="เปลี่ยนหน้ารายการ">
          <button
            type="button"
            className="action secondary"
            disabled={result.current <= 1}
            onClick={() => setPage(result.current - 1)}
          >
            ก่อนหน้า
          </button>
          <button
            type="button"
            className="action secondary"
            disabled={result.current >= result.pages}
            onClick={() => setPage(result.current + 1)}
          >
            ถัดไป
          </button>
        </div>
      )}
    </section>
  );
}
